import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  BarChart, Settings, TrendingUp, Mail, Users, 
  Calendar, LogOut, Save, RotateCw, Award, Languages, MapPin, DollarSign, Video,
  FileText, Database, HelpCircle, Target, Image, Search, Download, Megaphone, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import TranslationsEditor from '../components/TranslationsEditor';
import DestinationsEditor from '../components/DestinationsEditor';
import PriceCalculationEditor from '../components/PriceCalculationEditor';
import VideoManager from '../components/VideoManager';
import AnalyticsDashboard from '../components/AnalyticsDashboard';
import ContentEditor from '../components/ContentEditor';
import BackupManager from '../components/BackupManager';
import QuestionsEditor from '../components/QuestionsEditor';
import AlgorithmPlayground from '../components/AlgorithmPlayground';
import MediaManager from '../components/MediaManager';
import '../styles/AdminDashboard.css';
import '../styles/AnalyticsDashboard.css';
import '../styles/ContentEditor.css';
import '../styles/BackupManager.css';
import '../styles/QuestionsEditor.css';
import '../styles/AlgorithmPlayground.css';
import '../styles/MediaManager.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [isLoading, setIsLoading] = useState(true);
  
  // Data states
  const [overviewStats, setOverviewStats] = useState(null);
  const [answerStats, setAnswerStats] = useState(null);
  const [emailStats, setEmailStats] = useState(null);
  const [algorithmSettings, setAlgorithmSettings] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState(30);
  const [customers, setCustomers] = useState(null);
  const [customerSearch, setCustomerSearch] = useState('');
  const [ctaSettings, setCtaSettings] = useState(null);
  const [newsletterSubs, setNewsletterSubs] = useState(null);

  // Check if user is logged in
  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin');
      return;
    }
    
    loadData();
  }, []);

  const getAuthHeader = () => {
    const token = localStorage.getItem('admin_token');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/admin/logout`, {}, getAuthHeader());
    } catch (error) {
      console.error('Logout error:', error);
    }
    localStorage.removeItem('admin_token');
    navigate('/admin');
  };

  const loadData = async () => {
    setIsLoading(true);
    try {
      // Load overview stats
      const overviewRes = await axios.get(`${API}/admin/stats/overview`, getAuthHeader());
      setOverviewStats(overviewRes.data);

      // Load algorithm settings
      const algoRes = await axios.get(`${API}/admin/algorithm`, getAuthHeader());
      setAlgorithmSettings(algoRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      if (error.response?.status === 401) {
        toast.error('Sitzung abgelaufen. Bitte erneut anmelden.');
        localStorage.removeItem('admin_token');
        navigate('/admin');
      } else {
        toast.error('Fehler beim Laden der Daten');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const loadAnswerStats = async (days) => {
    try {
      const res = await axios.get(
        `${API}/admin/stats/answers${days ? `?days=${days}` : ''}`,
        getAuthHeader()
      );
      setAnswerStats(res.data);
    } catch (error) {
      console.error('Error loading answer stats:', error);
      toast.error('Fehler beim Laden der Antwortstatistiken');
    }
  };

  const loadEmailStats = async (days) => {
    try {
      const res = await axios.get(
        `${API}/admin/stats/emails${days ? `?days=${days}` : ''}`,
        getAuthHeader()
      );
      setEmailStats(res.data);
    } catch (error) {
      console.error('Error loading email stats:', error);
      toast.error('Fehler beim Laden der E-Mail-Statistiken');
    }
  };

 useEffect(() => {
    if (activeTab === 'statistics' && !answerStats) {
      loadAnswerStats(selectedPeriod);
      loadEmailStats(selectedPeriod);
    }
    if (activeTab === 'customers' && !customers) {
      loadCustomers();
    }
    if (activeTab === 'marketing') {
      loadMarketing();
    }
  }, [activeTab]);

  const loadMarketing = async () => {
    try {
      const [ctaRes, nlRes] = await Promise.all([
        axios.get(`${API}/admin/cta-settings`, getAuthHeader()),
        axios.get(`${API}/admin/newsletter/subscribers`, getAuthHeader())
      ]);
      setCtaSettings(ctaRes.data);
      setNewsletterSubs(nlRes.data);
    } catch (error) {
      console.error('Error loading marketing data:', error);
    }
  };

  const saveCTA = async () => {
    try {
      await axios.put(`${API}/admin/cta-settings`, ctaSettings, getAuthHeader());
      toast.success('CTA-Einstellungen gespeichert!');
    } catch (error) {
      toast.error('Fehler beim Speichern');
    }
  };

  const loadCustomers = async (search) => {
    try {
      const params = search ? `?search=${encodeURIComponent(search)}` : '';
      const res = await axios.get(`${API}/admin/customers${params}`, getAuthHeader());
      setCustomers(res.data);
    } catch (error) {
      console.error('Error loading customers:', error);
      toast.error('Fehler beim Laden der Kundenliste');
    }
  };

  const exportCustomersCSV = () => {
    if (!customers?.customers?.length) return;
    const rows = [['Name', 'Email', 'Stadt', 'Destinationen', 'Sprache', 'Datum']];
    customers.customers.forEach(c => {
      rows.push([
        c.name || '',
        c.email || '',
        c.city || '',
        (c.destinations || []).join('; '),
        c.language || '',
        c.sent_at ? new Date(c.sent_at).toLocaleDateString('de-DE') : ''
      ]);
    });
    const csv = rows.map(r => r.map(v => `"${v}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `kunden_${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handlePeriodChange = (days) => {
    setSelectedPeriod(days);
    loadAnswerStats(days);
    loadEmailStats(days);
  };

  const handleAlgorithmUpdate = async () => {
    try {
      await axios.put(`${API}/admin/algorithm`, algorithmSettings, getAuthHeader());
      toast.success('Einstellungen erfolgreich gespeichert!');
    } catch (error) {
      console.error('Error updating algorithm:', error);
      toast.error('Fehler beim Speichern der Einstellungen');
    }
  };

  const updateSetting = (key, value) => {
    setAlgorithmSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  if (isLoading) {
    return (
      <div className="admin-loading">
        <RotateCw size={48} className="spin" />
        <p>Lade Admin Panel...</p>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      {/* Header */}
      <div className="admin-header">
        <div className="admin-header-left">
          <h1>🎊 Admin Panel</h1>
          <p>Wedding-O-Mat Verwaltung</p>
        </div>
        <button className="admin-logout-button" onClick={handleLogout}>
          <LogOut size={20} />
          Abmelden
        </button>
      </div>

      {/* Tabs */}
      <div className="admin-tabs">
        <button
          className={`admin-tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          <TrendingUp size={20} />
          Übersicht
        </button>
        <button
          className={`admin-tab ${activeTab === 'statistics' ? 'active' : ''}`}
          onClick={() => setActiveTab('statistics')}
        >
          <BarChart size={20} />
          Statistiken
        </button>
        <button
          className={`admin-tab ${activeTab === 'customers' ? 'active' : ''}`}
          onClick={() => setActiveTab('customers')}
          data-testid="customers-tab"
        >
          <Users size={20} />
          Kunden
        </button>
        <button
          className={`admin-tab ${activeTab === 'marketing' ? 'active' : ''}`}
          onClick={() => setActiveTab('marketing')}
          data-testid="marketing-tab"
        >
          <Megaphone size={20} />
          Marketing
        </button>
        <button
          className={`admin-tab ${activeTab === 'analytics' ? 'active' : ''}`}
          onClick={() => setActiveTab('analytics')}
        >
          <TrendingUp size={20} />
          Analytics
        </button>
        <button
          className={`admin-tab ${activeTab === 'playground' ? 'active' : ''}`}
          onClick={() => setActiveTab('playground')}
        >
          <Target size={20} />
          Playground
        </button>
        <button
          className={`admin-tab ${activeTab === 'questions' ? 'active' : ''}`}
          onClick={() => setActiveTab('questions')}
        >
          <HelpCircle size={20} />
          Fragen
        </button>
        <button
          className={`admin-tab ${activeTab === 'media' ? 'active' : ''}`}
          onClick={() => setActiveTab('media')}
        >
          <Image size={20} />
          Media
        </button>
        <button
          className={`admin-tab ${activeTab === 'translations' ? 'active' : ''}`}
          onClick={() => setActiveTab('translations')}
        >
          <Languages size={20} />
          Übersetzungen
        </button>
        <button
          className={`admin-tab ${activeTab === 'destinations' ? 'active' : ''}`}
          onClick={() => setActiveTab('destinations')}
        >
          <MapPin size={20} />
          Destinationen
        </button>
        <button
          className={`admin-tab ${activeTab === 'prices' ? 'active' : ''}`}
          onClick={() => setActiveTab('prices')}
        >
          <DollarSign size={20} />
          Preisberechnung
        </button>
        <button
          className={`admin-tab ${activeTab === 'videos' ? 'active' : ''}`}
          onClick={() => setActiveTab('videos')}
        >
          <Video size={20} />
          Videos
        </button>
        <button
          className={`admin-tab ${activeTab === 'content' ? 'active' : ''}`}
          onClick={() => setActiveTab('content')}
        >
          <FileText size={20} />
          Content
        </button>
        <button
          className={`admin-tab ${activeTab === 'backup' ? 'active' : ''}`}
          onClick={() => setActiveTab('backup')}
        >
          <Database size={20} />
          Backup
        </button>
      </div>

      {/* Content */}
      <div className="admin-content">
        {/* Overview Tab */}
        {activeTab === 'overview' && overviewStats && (
          <div className="admin-overview">
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-icon users">
                  <Users size={28} />
                </div>
                <div className="stat-info">
                  <h3>Gesamt Quiz-Durchläufe</h3>
                  <p className="stat-value">{overviewStats.total_submissions}</p>
                  <span className="stat-badge">
                    <Calendar size={14} />
                    Letzte 7 Tage: {overviewStats.recent_submissions_7d}
                  </span>
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-icon emails">
                  <Mail size={28} />
                </div>
                <div className="stat-info">
                  <h3>Versendete E-Mails</h3>
                  <p className="stat-value">{overviewStats.total_emails}</p>
                  <span className="stat-badge">
                    <Calendar size={14} />
                    Letzte 7 Tage: {overviewStats.recent_emails_7d}
                  </span>
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-icon languages" style={{background: 'linear-gradient(135deg, #FA8BFF 0%, #2BD2FF 100%)'}}>
                  <span style={{fontSize: '28px'}}>🌍</span>
                </div>
                <div className="stat-info">
                  <h3>Sprachen</h3>
                  <div className="language-stats">
                    {overviewStats.language_stats && Object.entries(overviewStats.language_stats).map(([lang, count]) => (
                      <div key={lang} className="language-stat-item">
                        <span className="lang-flag">
                          {lang === 'de' ? '🇩🇪' : lang === 'en' ? '🇬🇧' : lang === 'es' ? '🇪🇸' : '🌐'}
                        </span>
                        <span className="lang-code">{lang.toUpperCase()}</span>
                        <span className="lang-count">{count}x</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="stat-card full-width">
                <div className="stat-icon destinations">
                  <Award size={28} />
                </div>
                <div className="stat-info">
                  <h3>Top 10 Destinationen</h3>
                  <div className="top-destinations-list">
                    {overviewStats.top_destinations.map((dest, index) => (
                      <div key={index} className="top-dest-item">
                        <span className="dest-rank">#{index + 1}</span>
                        <span className="dest-name">{dest.name}</span>
                        <span className="dest-count">{dest.count}x</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Statistics Tab */}
        {activeTab === 'statistics' && (
          <div className="admin-statistics">
            <div className="stats-header">
              <h2>Detaillierte Statistiken</h2>
              <div className="stats-header-actions">
                <div className="period-selector">
                  <button
                    className={selectedPeriod === 7 ? 'active' : ''}
                    onClick={() => handlePeriodChange(7)}
                  >
                    7 Tage
                  </button>
                  <button
                    className={selectedPeriod === 30 ? 'active' : ''}
                    onClick={() => handlePeriodChange(30)}
                  >
                    30 Tage
                  </button>
                  <button
                    className={selectedPeriod === 90 ? 'active' : ''}
                    onClick={() => handlePeriodChange(90)}
                  >
                    90 Tage
                  </button>
                  <button
                    className={selectedPeriod === null ? 'active' : ''}
                    onClick={() => handlePeriodChange(null)}
                  >
                    Alle Zeit
                  </button>
                </div>
                <button
                  className="reset-stats-btn"
                  data-testid="reset-stats-btn"
                  onClick={async () => {
                    if (window.confirm('Statistiken wirklich zurücksetzen? Alle bisherigen Quiz-Tracking-Daten werden gelöscht.')) {
                      try {
                        const res = await axios.post(`${API}/admin/stats/reset`, {}, getAuthHeader());
                        toast.success(res.data.message);
                        loadData();
                        loadAnswerStats(selectedPeriod);
                      } catch (error) {
                        toast.error('Fehler beim Zurücksetzen');
                      }
                    }
                  }}
                >
                  Statistiken zurücksetzen
                </button>
              </div>
            </div>

            {answerStats && (
              <div className="answer-stats-section">
                <h3>Antwort-Häufigkeiten</h3>
                <p className="stats-subtitle">
                  Basierend auf {answerStats.total_submissions} Quiz-Durchläufen
                </p>
                
                <div className="qs-grid" data-testid="question-stats-grid">
                  {Object.entries(answerStats.answer_stats).map(([questionId, answers]) => (
                    <div key={questionId} className="qs-card" data-testid={`stat-card-${questionId}`}>
                      <div className="qs-card-title">{questionId.toUpperCase()}</div>
                      <div className="qs-answers">
                        {Object.entries(answers)
                          .sort((a, b) => b[1].count - a[1].count)
                          .map(([answer, data]) => (
                            <div key={answer} className="qs-row" data-testid={`stat-row-${questionId}-${answer}`}>
                              <div className="qs-row-header">
                                <span className="qs-row-label">{answer}</span>
                                <span className="qs-row-pct">{data.percentage}%</span>
                              </div>
                              <div className="qs-bar-bg">
                                <div
                                  className="qs-bar-fg"
                                  style={{ width: `${Math.min(data.percentage, 100)}%` }}
                                />
                              </div>
                              <span className="qs-row-count">{data.count} Antworten</span>
                            </div>
                          ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {emailStats && (
              <div className="email-stats-section">
                <h3>E-Mail Versand</h3>
                <p className="stats-subtitle">
                  {emailStats.total_emails} E-Mails versendet
                </p>
                
                <div className="email-list">
                  {emailStats.emails.slice(0, 50).map((email, index) => (
                    <div key={index} className="email-item">
                      <div className="email-icon">
                        <Mail size={18} />
                      </div>
                      <div className="email-info">
                        <span className="email-address">{email.email}</span>
                        <span className="email-destinations">
                          Destinations: {email.destinations.join(', ')}
                        </span>
                      </div>
                      <span className="email-date">
                        {new Date(email.sent_at).toLocaleDateString('de-DE')}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Marketing Tab */}
        {activeTab === 'marketing' && (
          <div className="admin-marketing" data-testid="marketing-section">
            {/* CTA Settings */}
            <div className="marketing-section">
              <h2>Call-to-Action Button</h2>
              <p className="section-desc">Dieser Button wird oben auf der Ergebnisseite und in der Email angezeigt.</p>
              
              {ctaSettings && (
                <div className="cta-form">
                  <div className="cta-field">
                    <label>Button-Text (Deutsch)</label>
                    <input
                      type="text"
                      value={ctaSettings.button_text_de || ''}
                      onChange={(e) => setCtaSettings(prev => ({...prev, button_text_de: e.target.value}))}
                      data-testid="cta-text-de"
                    />
                  </div>
                  <div className="cta-field">
                    <label>Button-Text (English)</label>
                    <input
                      type="text"
                      value={ctaSettings.button_text_en || ''}
                      onChange={(e) => setCtaSettings(prev => ({...prev, button_text_en: e.target.value}))}
                      data-testid="cta-text-en"
                    />
                  </div>
                  <div className="cta-field">
                    <label>Button-Text (Español)</label>
                    <input
                      type="text"
                      value={ctaSettings.button_text_es || ''}
                      onChange={(e) => setCtaSettings(prev => ({...prev, button_text_es: e.target.value}))}
                      data-testid="cta-text-es"
                    />
                  </div>
                  <div className="cta-field">
                    <label>Ziel-URL</label>
                    <div className="cta-url-row">
                      <ExternalLink size={16} />
                      <input
                        type="url"
                        value={ctaSettings.button_url || ''}
                        onChange={(e) => setCtaSettings(prev => ({...prev, button_url: e.target.value}))}
                        placeholder="https://www.animooweddings.com/kontakt"
                        data-testid="cta-url"
                      />
                    </div>
                  </div>
                  <div className="cta-field cta-toggle">
                    <label>
                      <input
                        type="checkbox"
                        checked={ctaSettings.enabled !== false}
                        onChange={(e) => setCtaSettings(prev => ({...prev, enabled: e.target.checked}))}
                      />
                      Button aktiv
                    </label>
                  </div>
                  <button className="btn-save-cta" onClick={saveCTA} data-testid="save-cta-btn">
                    <Save size={16} /> Speichern
                  </button>
                </div>
              )}
            </div>

{/* Newsletter Settings */}
            <div className="marketing-section">
              <h2>Newsletter-Einstellungen</h2>
              <p className="section-desc">Text für die Newsletter-Anmeldung auf der Webseite und im Email-Formular.</p>
              
              {ctaSettings && (
                <div className="cta-form">
                  <div className="cta-field">
                    <label>Anmelde-Text (Deutsch)</label>
                    <input
                      type="text"
                      value={ctaSettings.newsletter_text_de || 'Ich möchte über neue Destinationen & Angebote informiert werden'}
                      onChange={(e) => setCtaSettings(prev => ({...prev, newsletter_text_de: e.target.value}))}
                      data-testid="nl-text-de"
                    />
                  </div>
                  <div className="cta-field">
                    <label>Anmelde-Text (English)</label>
                    <input
                      type="text"
                      value={ctaSettings.newsletter_text_en || 'I want to be informed about new destinations & offers'}
                      onChange={(e) => setCtaSettings(prev => ({...prev, newsletter_text_en: e.target.value}))}
                      data-testid="nl-text-en"
                    />
                  </div>
                  <div className="cta-field">
                    <label>Anmelde-Text (Español)</label>
                    <input
                      type="text"
                      value={ctaSettings.newsletter_text_es || 'Quiero recibir información sobre nuevos destinos y ofertas'}
                      onChange={(e) => setCtaSettings(prev => ({...prev, newsletter_text_es: e.target.value}))}
                      data-testid="nl-text-es"
                    />
                  </div>
                  <div className="cta-field">
                    <label>Footer-Titel (Deutsch)</label>
                    <input
                      type="text"
                      value={ctaSettings.newsletter_footer_de || 'Neue Destinationen & Angebote erhalten'}
                      onChange={(e) => setCtaSettings(prev => ({...prev, newsletter_footer_de: e.target.value}))}
                    />
                  </div>
                  <div className="cta-field">
                    <label>Footer-Titel (English)</label>
                    <input
                      type="text"
                      value={ctaSettings.newsletter_footer_en || 'Get new destinations & offers'}
                      onChange={(e) => setCtaSettings(prev => ({...prev, newsletter_footer_en: e.target.value}))}
                    />
                  </div>
                  <div className="cta-field">
                    <label>Footer-Titel (Español)</label>
                    <input
                      type="text"
                      value={ctaSettings.newsletter_footer_es || 'Recibe nuevos destinos y ofertas'}
                      onChange={(e) => setCtaSettings(prev => ({...prev, newsletter_footer_es: e.target.value}))}
                    />
                  </div>
                  <button className="btn-save-cta" onClick={saveCTA} data-testid="save-nl-btn">
                    <Save size={16} /> Speichern
                  </button>
                </div>
              )}
            </div>

            {/* Newsletter Subscribers */}
            <div className="marketing-section">
              <div className="newsletter-header">
                <h2>Newsletter-Abonnenten</h2>
                <a
                  href={`${API}/admin/newsletter/export-csv`}
                  className="csv-export-btn"
                  data-testid="newsletter-export-btn"
                >
                  <Download size={16} /> CSV für Mailchimp
                </a>
              </div>
              <p className="section-desc">Mailchimp-Import: CSV herunterladen und unter "Audience &gt; Import contacts" hochladen.</p>

              {newsletterSubs && (
                <>
                  <p className="customers-count">{newsletterSubs.total} Abonnenten</p>
                  <div className="customers-table-wrapper">
                    <table className="customers-table" data-testid="newsletter-table">
                      <thead>
                        <tr>
                          <th>Email</th>
                          <th>Name</th>
                          <th>Quelle</th>
                          <th>Datum</th>
                        </tr>
                      </thead>
                      <tbody>
                        {newsletterSubs.subscribers.map((s, i) => (
                          <tr key={i}>
                            <td>{s.email}</td>
                            <td>{s.name || '-'}</td>
                            <td><span className="dest-tag">{s.source}</span></td>
                            <td>{s.subscribed_at ? new Date(s.subscribed_at).toLocaleDateString('de-DE') : '-'}</td>
                          </tr>
                        ))}
                        {newsletterSubs.subscribers.length === 0 && (
                          <tr><td colSpan="4" style={{textAlign:'center', padding:'40px', color:'#999'}}>Noch keine Abonnenten</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Algorithm Tab removed - weights are now managed per question in the Fragen tab */}

        {/* Customers Tab */}
        {activeTab === 'customers' && (
          <div className="admin-customers" data-testid="customers-section">
            <div className="customers-header">
              <h2>Kundenliste</h2>
              <div className="customers-actions">
                <div className="customers-search">
                  <Search size={18} />
                  <input
                    type="text"
                    placeholder="Name, Email oder Stadt suchen..."
                    value={customerSearch}
                    onChange={(e) => {
                      setCustomerSearch(e.target.value);
                      loadCustomers(e.target.value || undefined);
                    }}
                    data-testid="customer-search-input"
                  />
                </div>
                <button
                  className="csv-export-btn"
                  onClick={exportCustomersCSV}
                  data-testid="csv-export-btn"
                >
                  <Download size={18} />
                  CSV Export
                </button>
              </div>
            </div>

            {customers && (
              <>
                <p className="customers-count">{customers.total} Kunden gefunden</p>
                <div className="customers-table-wrapper">
                  <table className="customers-table" data-testid="customers-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Stadt</th>
                        <th>Destinationen</th>
                        <th>Sprache</th>
                        <th>Datum</th>
                      </tr>
                    </thead>
                    <tbody>
                      {customers.customers.map((c, i) => (
                        <tr key={i} data-testid={`customer-row-${i}`}>
                          <td>{c.name || '-'}</td>
                          <td>
                            <a href={`mailto:${c.email}`} className="customer-email-link">
                              {c.email}
                            </a>
                          </td>
                          <td>{c.city || '-'}</td>
                          <td className="customer-destinations">
                            {(c.destinations || []).map((d, j) => (
                              <span key={j} className="dest-tag">{d}</span>
                            ))}
                          </td>
                          <td>
                            <span className="lang-badge">
                              {c.language === 'de' ? 'DE' : c.language === 'en' ? 'EN' : c.language === 'es' ? 'ES' : c.language?.toUpperCase()}
                            </span>
                          </td>
                          <td>{c.sent_at ? new Date(c.sent_at).toLocaleDateString('de-DE') : '-'}</td>
                        </tr>
                      ))}
                      {customers.customers.length === 0 && (
                        <tr>
                          <td colSpan="6" style={{textAlign: 'center', padding: '40px', color: '#999'}}>
                            Keine Kunden gefunden
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}

        {/* Advanced Analytics Tab */}
        {activeTab === 'analytics' && (
          <AnalyticsDashboard 
            getAuthHeader={getAuthHeader}
            selectedPeriod={selectedPeriod}
          />
        )}

        {/* Translations Tab */}
        {activeTab === 'translations' && (
          <TranslationsEditor token={localStorage.getItem('admin_token')} />
        )}

        {/* Destinations Tab */}
        {activeTab === 'destinations' && (
          <DestinationsEditor token={localStorage.getItem('admin_token')} />
        )}

        {/* Price Calculation Tab */}
        {activeTab === 'prices' && (
          <PriceCalculationEditor token={localStorage.getItem('admin_token')} />
        )}

        {/* Videos Tab */}
        {activeTab === 'videos' && (
          <VideoManager />
        )}

        {/* Content Management Tab */}
        {activeTab === 'content' && (
          <ContentEditor token={localStorage.getItem('admin_token')} />
        )}

        {/* Backup Management Tab */}
        {activeTab === 'backup' && (
          <BackupManager token={localStorage.getItem('admin_token')} />
        )}

        {/* Algorithm Playground Tab */}
        {activeTab === 'playground' && (
          <AlgorithmPlayground token={localStorage.getItem('admin_token')} />
        )}

        {/* Questions Editor Tab */}
        {activeTab === 'questions' && (
          <QuestionsEditor token={localStorage.getItem('admin_token')} />
        )}

        {/* Media Manager Tab */}
        {activeTab === 'media' && (
          <MediaManager token={localStorage.getItem('admin_token')} />
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
